import type { ReactNode } from 'react';
import { useCan, useSubjectLanguageVariants } from '@features/user';
import SmsOutlined from '@mui/icons-material/SmsOutlined';
import { Box, Button, Collapse, Divider, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { FeatureGates, useFeatureGate } from '@services/statsig';

import { useRequestCopy } from '../store/useRequestCopy';

import { RequestCopyEditor } from './RequestCopyEditor';

/** Where the card lives, so a row's "Edit message" can bring it into view. */
export const REQUEST_MESSAGE_CARD_ID = 'patient-requirement-request-message';
/** The card's heading, which "Edit message" moves focus to. */
export const REQUEST_MESSAGE_HEADING_ID = `${REQUEST_MESSAGE_CARD_ID}-heading`;

export interface RequestCopySectionProps {
  /** Open state is lifted so any requirement's "Edit message" can open it. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export interface RequestMessageCardProps extends RequestCopySectionProps {
  canEdit: boolean;
  /** The editor, rendered only while the card is open. */
  children: ReactNode;
}

/** The summary row and its collapsible editor, with no store behind it. */
export const RequestMessageCard = ({
  open,
  onOpenChange,
  canEdit,
  children,
}: RequestMessageCardProps) => {
  const editorId = `${REQUEST_MESSAGE_CARD_ID}-editor`;
  return (
    <Paper
      id={REQUEST_MESSAGE_CARD_ID}
      variant="outlined"
      sx={{ borderRadius: 2, mt: 3, p: { xs: 2, md: 2.5 } }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
          gap: 2,
          alignItems: 'center',
        }}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
          <SmsOutlined color="action" sx={{ mt: 0.25 }} />
          <Box>
            <Typography
              id={REQUEST_MESSAGE_HEADING_ID}
              component="h2"
              variant="subtitle1"
              tabIndex={-1}
              sx={{ fontWeight: 600, '&:focus': { outline: 'none' } }}
            >
              Request message
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The text and email every request uses, for every requirement. A secure link is added
              to each message.
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="outlined"
          aria-expanded={open}
          aria-controls={editorId}
          onClick={() => onOpenChange(!open)}
          sx={{ justifySelf: { xs: 'start', md: 'end' } }}
        >
          {open ? 'Done' : canEdit ? 'Edit message' : 'View message'}
        </Button>
      </Box>
      <Collapse in={open} unmountOnExit id={editorId}>
        <Divider sx={{ my: 2 }} />
        {children}
      </Collapse>
    </Paper>
  );
};

/**
 * The practice's one request message, under the catalog it applies to
 * (AC-11, GDN-3230), behind a summary row (GDN-4142): the catalog leads, and
 * the message is one click away from the page and from every requirement.
 *
 * The draft lives in `useRequestCopy`, which this component holds whether or
 * not the editor is open, so closing it with an unsaved edit hides the edit
 * and does not drop it.
 *
 * Gated the same way as the catalog it sits under: the Statsig gate is
 * rollout, `manage_organization` is the practice-admin check. A provider who
 * reaches it without the permission reads the copy and edits nothing — the
 * per-launch preview is not admin-only, so the text itself is not privileged.
 */
export const RequestCopySection = ({ open, onOpenChange }: RequestCopySectionProps) => {
  const enabled = useFeatureGate(FeatureGates.PATIENT_REQUIREMENTS);
  const isAdmin = useCan('manage_organization');
  const subject = useSubjectLanguageVariants();

  const copy = useRequestCopy(enabled);

  if (!enabled) return null;

  return (
    <RequestMessageCard open={open} onOpenChange={onOpenChange} canEdit={isAdmin}>
      {copy.isLoading ? (
        <Skeleton variant="rounded" height={280} />
      ) : (
        <RequestCopyEditor
          draft={copy.draft}
          smsIsDefault={copy.smsIsDefault}
          emailIsDefault={copy.emailIsDefault}
          smsSegmentHint={copy.smsSegmentHint}
          linkPlaceholder={copy.linkPlaceholder}
          smsPreview={copy.smsPreview}
          previewIsStale={copy.previewIsStale}
          canEdit={isAdmin}
          status={copy.status}
          isDirty={copy.isDirty}
          errorMessage={copy.errorMessage}
          subject={subject}
          onChange={copy.onChange}
          onSave={copy.onSave}
          onResetChannel={copy.onResetChannel}
        />
      )}
    </RequestMessageCard>
  );
};
